import { useEffect, useRef, useState } from "react";
import { useWebSocket } from "@/context/WebSocketProvider";

const DEFAULT_MAX_POLL_INTERVAL_MS = 60_000;

// ±20%, so clients that lost the socket together don't poll in lockstep.
const withJitter = (ms: number) => Math.round(ms * (0.8 + Math.random() * 0.4));

/**
 * Connection-state-driven polling gate.
 *
 * WebSocket events are the primary update mechanism; polling is only a fallback
 * for when the socket is unhealthy. This hook is the single helper every
 * `refetchInterval` should call so the gate logic lives in exactly one place.
 *
 * While unhealthy the interval starts at `baseInterval` and doubles each period
 * up to `maxInterval`, with jitter. A socket that recovers and drops again
 * within `maxInterval` resumes at its previous interval instead of restarting
 * at `baseInterval`, so a flapping connection can't keep polling fast.
 *
 * @param baseInterval - the first polling interval (ms) once the socket is unhealthy.
 * @param maxInterval - the backoff ceiling (ms), before jitter. Jitter is applied
 *   on top so clients at the ceiling stay spread out rather than clipped to it.
 * @returns `false` when the socket is healthy (disable polling), otherwise the current interval.
 *
 * @example
 * useQuery({
 *   queryKey: ["integrations"],
 *   queryFn: fetchIntegrations,
 *   // Poll from 5s, backing off to 60s, only while the socket is down.
 *   refetchInterval: usePollingGate(5000),
 * });
 */
export const usePollingGate = (
  baseInterval: number,
  maxInterval: number = DEFAULT_MAX_POLL_INTERVAL_MS,
): number | false => {
  const { isHealthy } = useWebSocket();
  const [pollInterval, setPollInterval] = useState(baseInterval);
  const currentRef = useRef(baseInterval);
  const recoveredAtRef = useRef(-Infinity);

  useEffect(() => {
    if (isHealthy) {
      recoveredAtRef.current = Date.now();
      return;
    }

    const isFlapping = Date.now() - recoveredAtRef.current < maxInterval;
    let current = isFlapping
      ? Math.max(currentRef.current, baseInterval)
      : baseInterval;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const apply = () => {
      currentRef.current = current;
      setPollInterval(withJitter(current));
    };

    const step = () => {
      current = Math.min(current * 2, maxInterval);
      apply();
      if (current < maxInterval) timer = setTimeout(step, current);
    };

    apply();
    if (current < maxInterval) timer = setTimeout(step, current);

    return () => clearTimeout(timer);
  }, [isHealthy, baseInterval, maxInterval]);

  return isHealthy ? false : pollInterval;
};
